import { useCallback, useRef, useState } from "react";

const TARGET = 0.06;

const buildGraph = (ctx) => {
  const master = ctx.createGain();
  master.gain.value = 0.0001;
  master.connect(ctx.destination);
  const sources = [];

  // Low drone bed (two slightly detuned oscillators + airy octave)
  const drone = ctx.createGain();
  drone.gain.value = 0.5;
  drone.connect(master);
  const mkOsc = (type, freq, g) => {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    const og = ctx.createGain();
    og.gain.value = g;
    o.connect(og);
    og.connect(drone);
    sources.push(o);
  };
  mkOsc("sine", 54, 1);
  mkOsc("triangle", 54.6, 0.35);
  mkOsc("sine", 108, 0.1);

  // Wind / propeller air layer: looped noise through a slowly breathing lowpass
  const len = 2 * ctx.sampleRate;
  const buffer = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  noise.loop = true;
  const nFilter = ctx.createBiquadFilter();
  nFilter.type = "lowpass";
  nFilter.frequency.value = 300;
  nFilter.Q.value = 0.7;
  const noiseGain = ctx.createGain();
  noiseGain.gain.value = 0.14;
  noise.connect(nFilter);
  nFilter.connect(noiseGain);
  noiseGain.connect(master);
  sources.push(noise);

  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.08;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 150;
  lfo.connect(lfoGain);
  lfoGain.connect(nFilter.frequency);
  sources.push(lfo);

  // Soft sonar pings (measurement instrument vibe)
  const ping = (at, f0) => {
    const o = ctx.createOscillator();
    o.type = "sine";
    const g = ctx.createGain();
    o.frequency.setValueAtTime(f0, at);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.5, at + 0.9);
    g.gain.setValueAtTime(0.0001, at);
    g.gain.exponentialRampToValueAtTime(0.055, at + 0.06);
    g.gain.exponentialRampToValueAtTime(0.0001, at + 0.95);
    o.connect(g);
    g.connect(master);
    o.start(at);
    o.stop(at + 1);
  };
  ping(ctx.currentTime + 0.35, 760);
  ping(ctx.currentTime + 1.35, 560);

  sources.forEach((s) => s.start());
  const stopAll = (t) => {
    sources.forEach((s) => {
      try {
        s.stop(t);
      } catch {}
    });
  };
  return { master, stopAll };
};

export default function useIntroSound() {
  const ctxRef = useRef(null);
  const masterRef = useRef(null);
  const stopRef = useRef(null);
  const enabledRef = useRef(true);
  const [enabled, setEnabled] = useState(true);

  const close = useCallback(() => {
    if (ctxRef.current) {
      try {
        ctxRef.current.close();
      } catch {}
      ctxRef.current = null;
      masterRef.current = null;
      stopRef.current = null;
    }
  }, []);

  const fadeOut = useCallback(() => {
    const ctx = ctxRef.current;
    const master = masterRef.current;
    if (!ctx || !master) return;
    try {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setValueAtTime(Math.max(master.gain.value, 0.0001), t);
      master.gain.linearRampToValueAtTime(0.0001, t + 0.45);
      stopRef.current?.(t + 0.6);
    } catch {}
    setTimeout(close, 750);
  }, [close]);

  const start = useCallback(() => {
    if (!enabledRef.current) return;
    try {
      if (ctxRef.current && masterRef.current) {
        const ctx = ctxRef.current;
        ctx.resume();
        const t = ctx.currentTime;
        masterRef.current.gain.cancelScheduledValues(t);
        masterRef.current.gain.setValueAtTime(0.0001, t);
        masterRef.current.gain.linearRampToValueAtTime(TARGET, t + 0.6);
        return;
      }
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      const { master, stopAll } = buildGraph(ctx);
      ctxRef.current = ctx;
      masterRef.current = master;
      stopRef.current = stopAll;
      master.gain.linearRampToValueAtTime(TARGET, ctx.currentTime + 1.2);
      const tryResume = async () => {
        try {
          await ctx.resume();
        } catch {}
        return ctx.state === "running";
      };
      return tryResume();
    } catch {
      return undefined;
    }
  }, []);

  const toggle = useCallback(() => {
    setEnabled((e) => {
      const next = !e;
      enabledRef.current = next;
      if (next) start();
      else fadeOut();
      return next;
    });
  }, [start, fadeOut]);

  return { enabled, toggle, start, fadeOut };
}
